// MetricsDisplay.js - 展示结构化行为指标的组件
// 用法: import { MetricsDisplay } from './MetricsDisplay'
//       <MetricsDisplay metrics={result.metrics} />

import React from 'react';

const theme = {
  bg: '#0d1117',
  bgCard: '#161b22',
  border: '#30363d',
  teal: '#2dd4bf',
  cyan: '#22d3ee',
  purple: '#a855f7',
  text: '#e6edf3',
  textMuted: '#c9d1d9',
  textDark: '#8b949e',
  green: '#22c55e',
  red: '#f85149',
  neutral: '#d29922',
  blue: '#58a6ff',
};

// 圆环进度条组件 - 青绿色磨砂风格
function CircularProgress({ value, max = 100, size = 130, label }) {
  const percentage = Math.min(Math.max(value / max, 0), 1);
  const strokeWidth = 10;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - percentage * circumference;

  const gradientId = `gradient-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div style={{ textAlign: 'center' }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#2dd4bf" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#22d3ee" stopOpacity="0.7" />
          </linearGradient>
        </defs>
        {/* 背景圆 - 磨砂感 */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(45, 212, 191, 0.15)"
          strokeWidth={strokeWidth}
        />
        {/* 进度圆 - 渐变 */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={`url(#${gradientId})`}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.5s ease' }}
        />
      </svg>
      <div style={{ marginTop: '-90px', paddingBottom: '50px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
  <p style={{ color: theme.text, fontSize: '32px', fontWeight: 700, margin: 0, lineHeight: 1 }}>
    {value.toFixed(0)}
  </p>
  <p style={{ color: theme.textDark, fontSize: '14px', margin: '4px 0 0' }}>
    {label}
  </p>
</div>

    </div>
  );
}

// 简洁水平进度条 - 加大字体
function HorizontalBar({ label, value, maxValue = 100 }) {
  const percentage = Math.min(Math.max(value / maxValue, 0), 1) * 100;

  return (
    <div style={{ marginBottom: '24px' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '12px'
      }}>
        <span style={{ color: theme.textMuted, fontSize: '17px' }}>{label}</span>
        <span style={{
          color: theme.text,
          fontSize: '20px',
          fontWeight: 600,
          fontFeatureSettings: '"tnum"'
        }}>
          {value.toFixed(1)}%
        </span>
      </div>
      <div style={{
        height: '14px',
        background: 'rgba(45, 212, 191, 0.12)',
        borderRadius: '7px',
        overflow: 'hidden'
      }}>
        <div style={{
          width: `${percentage}%`,
          height: '100%',
          background: `linear-gradient(90deg, ${theme.teal}, ${theme.cyan})`,
          borderRadius: '7px',
          transition: 'width 0.5s ease'
        }} />
      </div>
    </div>
  );
}

// 简化的行为标签 - 加大字体
function BehaviorTag({ label, isWarning = false }) {
  const bg = isWarning ? 'rgba(248, 81, 73, 0.08)' : 'rgba(45, 212, 191, 0.08)';
  const color = isWarning ? 'rgba(248, 113, 113, 0.9)' : 'rgba(45, 212, 191, 0.9)';
  const borderColor = isWarning ? 'rgba(248, 81, 73, 0.3)' : 'rgba(45, 212, 191, 0.3)';

  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      background: bg,
      border: '1px solid ' + borderColor,
      borderRadius: '8px',
      padding: '10px 18px',
      fontSize: '15px',
      fontWeight: 500,
      color: color,
      marginRight: '12px',
      marginBottom: '12px',
    }}>
      {label}
    </span>
  );
}

// 主组件
export function MetricsDisplay({ metrics }) {
  if (!metrics) return null;

  // 从 metrics 中提取数据
  const summaryCards = metrics.summary_cards || [];
  const sentimentChart = metrics.sentiment_chart || {};
  const warnings = metrics.warnings || [];
  const tags = metrics.tags || [];
  const raw = metrics.raw || {};

  // 获取核心数值
  const timingScore = raw.sentiment_alignment?.overall_timing_score || 50;
  const positionConsistency = raw.position_consistency || 50;
  const chasesRalliesPct = raw.chases_rallies?.pct || 0;
  const panicSellsPct = raw.panic_sells?.pct || 0;
  const buyInBullishPct = raw.sentiment_alignment?.buy_in_bullish_pct || 0;
  const buyInBearishPct = raw.sentiment_alignment?.buy_in_bearish_pct || 0;
  const sellInBearishPct = raw.sentiment_alignment?.sell_in_bearish_pct || 0;
  const sellInBullishPct = raw.sentiment_alignment?.sell_in_bullish_pct || 0;

  // 警告标签映射
  const warningTags = [
    'momentum_chaser', 'panic_seller', 'poor_timing',
    'inconsistent_sizing', 'high_frequency_trader'
  ];

  return (
    <div style={{ marginBottom: '28px' }}>

      {/* ========== 核心评分卡片 ========== */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '20px',
        marginBottom: '24px'
      }}>
        {/* 时机评分 */}
        <div style={{
          background: theme.bgCard,
          border: '1px solid ' + theme.border,
          borderRadius: '14px',
          padding: '28px',
          textAlign: 'center'
        }}>
          <h4 style={{
            color: theme.textMuted,
            fontSize: '15px',
            margin: '0 0 20px',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            fontWeight: 500
          }}>
            Timing Score
          </h4>
          <CircularProgress
            value={timingScore}
            label="/ 100"
          />
          <p style={{
            color: theme.textDark,
            fontSize: '15px',
            margin: '8px 0 0',
            lineHeight: '1.5'
          }}>
            {timingScore >= 70 ? 'Great timing! Keep it up.' :
             timingScore >= 40 ? 'Room for improvement.' :
             'Your timing needs work.'}
          </p>
        </div>

        {/* 仓位一致性 */}
        <div style={{
          background: theme.bgCard,
          border: '1px solid ' + theme.border,
          borderRadius: '14px',
          padding: '28px',
          textAlign: 'center'
        }}>
          <h4 style={{
            color: theme.textMuted,
            fontSize: '15px',
            margin: '0 0 20px',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            fontWeight: 500
          }}>
            Position Consistency
          </h4>
          <CircularProgress
            value={positionConsistency}
            label="/ 100"
          />
          <p style={{
            color: theme.textDark,
            fontSize: '15px',
            margin: '8px 0 0',
            lineHeight: '1.5'
          }}>
            {positionConsistency >= 70 ? 'Consistent sizing.' :
             positionConsistency >= 40 ? 'Somewhat variable.' :
             'Very inconsistent sizing.'}
          </p>
        </div>

        {/* 交易统计 */}
        <div style={{
          background: theme.bgCard,
          border: '1px solid ' + theme.border,
          borderRadius: '14px',
          padding: '28px'
        }}>
          <h4 style={{
            color: theme.textMuted,
            fontSize: '15px',
            margin: '0 0 20px',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            fontWeight: 500
          }}>
            Trading Stats
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: theme.textMuted, fontSize: '16px' }}>Trading Days</span>
              <span style={{ color: theme.text, fontSize: '16px', fontWeight: 600 }}>
                {raw.trading_days || 0}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: theme.textMuted, fontSize: '16px' }}>Avg Trades/Day</span>
              <span style={{ color: theme.text, fontSize: '16px', fontWeight: 600 }}>
                {raw.avg_trades_per_day || 0}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: theme.textMuted, fontSize: '16px' }}>Avg Position</span>
              <span style={{ color: theme.text, fontSize: '16px', fontWeight: 600 }}>
                ${(raw.avg_position_size || 0).toLocaleString()}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: theme.textMuted, fontSize: '16px' }}>Symbols</span>
              <span style={{ color: theme.text, fontSize: '16px', fontWeight: 600 }}>
                {raw.unique_symbols || 0}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ========== 情绪匹配度 - 新设计 ========== */}
      <div style={{
        background: theme.bgCard,
        border: '1px solid ' + theme.border,
        borderRadius: '14px',
        padding: '32px',
        marginBottom: '24px'
      }}>
        <h4 style={{
          color: theme.text,
          fontSize: '18px',
          fontWeight: 600,
          margin: '0 0 28px',
        }}>
          Sentiment Alignment
        </h4>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '48px' }}>
          {/* 买入时机 */}
          <div>
            <h5 style={{
              color: theme.textMuted,
              fontSize: '14px',
              margin: '0 0 24px',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              fontWeight: 500
            }}>
              Buy Timing
            </h5>
            <HorizontalBar label="In Bullish Zones" value={buyInBullishPct} />
            <HorizontalBar label="In Bearish Zones" value={buyInBearishPct} />
          </div>

          {/* 卖出时机 */}
          <div>
            <h5 style={{
              color: theme.textMuted,
              fontSize: '14px',
              margin: '0 0 24px',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              fontWeight: 500
            }}>
              Sell Timing
            </h5>
            <HorizontalBar label="In Bearish Zones" value={sellInBearishPct} />
            <HorizontalBar label="In Bullish Zones" value={sellInBullishPct} />
          </div>
        </div>
      </div>

      {/* ========== 行为模式检测 - 简化设计 ========== */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '20px',
        marginBottom: '24px'
      }}>
        {/* 追涨检测 */}
        <div style={{
          background: theme.bgCard,
          border: '1px solid ' + theme.border,
          borderRadius: '14px',
          padding: '28px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
            <h4 style={{ color: theme.text, fontSize: '18px', fontWeight: 600, margin: 0 }}>
              Momentum Chasing
            </h4>
            {chasesRalliesPct > 40 && (
              <span style={{
                background: 'rgba(248, 81, 73, 0.15)',
                color: 'rgba(248, 113, 113, 0.9)',
                padding: '6px 14px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: 600,
                letterSpacing: '0.5px'
              }}>
                DETECTED
              </span>
            )}
          </div>
          <p style={{ color: theme.textDark, fontSize: '16px', margin: '0 0 20px' }}>
            Buying after 3+ consecutive up days
          </p>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
            <span style={{
              color: theme.text,
              fontSize: '42px',
              fontWeight: 700
            }}>
              {chasesRalliesPct.toFixed(1)}%
            </span>
            <span style={{ color: theme.textDark, fontSize: '16px' }}>of your buys</span>
          </div>
        </div>

        {/* 恐慌卖出检测 */}
        <div style={{
          background: theme.bgCard,
          border: '1px solid ' + theme.border,
          borderRadius: '14px',
          padding: '28px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
            <h4 style={{ color: theme.text, fontSize: '18px', fontWeight: 600, margin: 0 }}>
              Panic Selling
            </h4>
            {panicSellsPct > 30 && (
              <span style={{
                background: 'rgba(248, 81, 73, 0.15)',
                color: 'rgba(248, 113, 113, 0.9)',
                padding: '6px 14px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: 600,
                letterSpacing: '0.5px'
              }}>
                DETECTED
              </span>
            )}
          </div>
          <p style={{ color: theme.textDark, fontSize: '16px', margin: '0 0 20px' }}>
            Selling on days with &gt;3% drops
          </p>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
            <span style={{
              color: theme.text,
              fontSize: '42px',
              fontWeight: 700
            }}>
              {panicSellsPct.toFixed(1)}%
            </span>
            <span style={{ color: theme.textDark, fontSize: '16px' }}>of your sells</span>
          </div>
        </div>
      </div>

      {/* ========== 行为标签 - 简化 ========== */}
      {tags.length > 0 && (
        <div style={{
          background: theme.bgCard,
          border: '1px solid ' + theme.border,
          borderRadius: '14px',
          padding: '28px'
        }}>
          <h4 style={{
            color: theme.text,
            fontSize: '18px',
            fontWeight: 600,
            margin: '0 0 20px'
          }}>
            Behavior Tags
          </h4>
          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            {tags.map((tag, i) => {
              const isWarning = warningTags.includes(tag);
              const label = tag.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
              return <BehaviorTag key={i} label={label} isWarning={isWarning} />;
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default MetricsDisplay;